export interface TelecallingCampaign {
  id: string;
  title: string;
  category: "outbound" | "inbound" | "tech_support" | "collections";
  tagline: string;
  description: string;
  features: string[];
  baseMonthlyRateInr: number;
  baseMonthlyRateUsd: number;
  badge: string;
  recommendedDialer: string;
  slaUptime: string;
}

export const TELECALLING_CAMPAIGNS: TelecallingCampaign[] = [
  {
    id: "b2b-outbound",
    title: "High-Velocity B2B Lead Generation",
    category: "outbound",
    tagline: "Cold calling and appointment setting that books qualified discovery meetings with enterprise decision-makers.",
    description: "Dedicated SDR pods equipped with AI power dialers, custom call cadence engineering, and verified B2B prospect lists.",
    features: [
      "Custom objection handling scripts & battlecards",
      "Real-time CRM calendar sync with Account Executives",
      "Daily call recordings & QA sentiment analysis",
      "Dedicated Team Lead & QA auditor oversight"
    ],
    baseMonthlyRateInr: 32000,
    baseMonthlyRateUsd: 1400,
    badge: "Most Popular",
    recommendedDialer: "AI Predictive & Power Dialer",
    slaUptime: "99.8% Dialing Uptime"
  },
  {
    id: "inbound-care",
    title: "24/7 Omnichannel Customer Care",
    category: "inbound",
    tagline: "Always-on inbound voice, chat, and ticketing support for rapid First Call Resolution.",
    description: "Multi-lingual agents trained on your knowledge base delivering empathy-led, compliant customer support around the clock.",
    features: [
      "Average Speed of Answer (ASA) < 15 seconds",
      "Tier-1 voice, WhatsApp, live chat & email resolution",
      "Customer CSAT & NPS benchmarking reports",
      "Seamless Zendesk / Freshdesk / Salesforce integration"
    ],
    baseMonthlyRateInr: 28000,
    baseMonthlyRateUsd: 1250,
    badge: "24/7 Coverage",
    recommendedDialer: "ACD Skill-Based Intelligent Routing",
    slaUptime: "99.9% Uptime Guarantee"
  },
  {
    id: "tech-support",
    title: "Tier-2 Technical Helpdesk & Product Support",
    category: "tech_support",
    tagline: "Engineers and specialized tech support specialists resolving complex product and SaaS issues.",
    description: "High-acuity support teams handling software troubleshooting, API inquiries, account integrations, and hardware RMA workflows.",
    features: [
      "Screen sharing & diagnostic log analysis",
      "Escalation pathways to client core engineering",
      "Strict SLA response windows (P1 under 15 mins)",
      "Continuous runbook & documentation updates"
    ],
    baseMonthlyRateInr: 42000,
    baseMonthlyRateUsd: 1850,
    badge: "High Acuity",
    recommendedDialer: "Interactive Voice & Screen Diagnostic System",
    slaUptime: "99.95% Enterprise SLA"
  },
  {
    id: "debt-recovery",
    title: "Fintech Loan Servicing & Soft Collections",
    category: "collections",
    tagline: "Ethical, compliant early-bucket debt recovery and reminder outreach protecting brand reputation.",
    description: "RBI & TCPA compliant recovery agents focused on friendly payment schedule negotiation, digital link dispatch, and reconciliation.",
    features: [
      "100% compliant call recording & audit archives",
      "Instant UPI & payment link dispatch during live call",
      "Early bucket (DPD 1-30 & 31-60) recovery focus",
      "Automated skip tracing and phone validation"
    ],
    baseMonthlyRateInr: 35000,
    baseMonthlyRateUsd: 1550,
    badge: "Strictly Compliant",
    recommendedDialer: "Preview & Verified Omnichannel Dialer",
    slaUptime: "100% Regulatory Audit Trail"
  }
];

export const TELECALLING_STAGES = [
  { id: "raw_leads", name: "Raw Ingested Leads", color: "bg-slate-500/10 text-slate-400 border-slate-500/20" },
  { id: "call_connected", name: "Call Connected", color: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20" },
  { id: "qualified_interested", name: "Qualified & Interested", color: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
  { id: "demo_scheduled", name: "Demo / Discovery Scheduled", color: "bg-purple-500/10 text-purple-400 border-purple-500/20" },
  { id: "deal_won", name: "Deal Closed / Retainer Won", color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  { id: "dnd_lost", name: "Do Not Call / Lost", color: "bg-rose-500/10 text-rose-400 border-rose-500/20" }
] as const;

export const CALL_DISPOSITIONS = [
  { id: "interested", label: "Interested / Decision Maker Receptive", color: "bg-emerald-500/20 text-emerald-300" },
  { id: "callback", label: "Call Back Requested", color: "bg-amber-500/20 text-amber-300" },
  { id: "voicemail", label: "Left Voicemail / Drop Note", color: "bg-blue-500/20 text-blue-300" },
  { id: "busy", label: "Busy / Gatekeeper Block", color: "bg-orange-500/20 text-orange-300" },
  { id: "dnd", label: "Do Not Call / Unsubscribed", color: "bg-rose-500/20 text-rose-300" }
] as const;

export const BPO_AGENTS = [
  { id: "AGT-101", name: "Priya Sharma", role: "Senior B2B Closer SDR", rating: "4.9/5", callsToday: 82, conversionRate: "14.2%" },
  { id: "AGT-102", name: "David Miller", role: "Enterprise Outbound Specialist", rating: "4.8/5", callsToday: 94, conversionRate: "12.8%" },
  { id: "AGT-103", name: "Aakash Verma", role: "Team Lead & QA Assessor", rating: "4.95/5", callsToday: 45, conversionRate: "18.5%" },
  { id: "AGT-104", name: "Ananya Iyer", role: "Inbound Escalation Specialist", rating: "4.85/5", callsToday: 76, conversionRate: "16.0%" }
];

export function calculateIndicativeRetainer(
  campaignId: string,
  seatCount: number,
  shiftType: "day" | "night" | "247",
  currency: "INR" | "USD"
): { perSeatRate: number; monthlyTotal: number; callsTargetPerMonth: number; hoursMonthly: number } {
  const campaign = TELECALLING_CAMPAIGNS.find((c) => c.id === campaignId) || TELECALLING_CAMPAIGNS[0];
  const baseRate = currency === "INR" ? campaign.baseMonthlyRateInr : campaign.baseMonthlyRateUsd;

  let shiftMultiplier = 1.0;
  if (shiftType === "night") shiftMultiplier = 1.25;
  if (shiftType === "247") shiftMultiplier = 2.4;

  const perSeatRate = Math.round(baseRate * shiftMultiplier);
  const monthlyTotal = perSeatRate * Math.max(1, seatCount);

  // Benchmarks
  const dailyCallsPerSeat = campaign.category === "outbound" ? 90 : 65;
  const callsTargetPerMonth = dailyCallsPerSeat * 22 * seatCount;
  const hoursMonthly = (shiftType === "247" ? 720 : 176) * seatCount;

  return {
    perSeatRate,
    monthlyTotal,
    callsTargetPerMonth,
    hoursMonthly
  };
}
